import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { useNavigate } from 'react-router';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import Button from 'components/Button';
import { useDebounceFn } from 'hooks/useDebounceFn';
import { useGlobalState } from 'hooks/useGlobalState';
import { requiredErrorMsg, unexpectedServerErrorMsg } from 'utils/message';
import { hostnamePrefixRegex } from 'hostedFreePages/Onboarding/components/OnboardingForm';

import styles from './OnboardingForm.module.scss';
import InputField from './InputField';

export const passwordNotMatchMsg = "Passwords don't match.";
export const hostnameExistedMsg = 'That hostname is not available.';
export const hostnameMalformedMsg =
  'Invalid instance name. The name can only include numbers, letters, and hyphens.';

const postForm = (data) =>
  axios.post('xm/free/shared/api/completeSignup', data);

export default function OnboardingForm({ signupData, setApiError }) {
  const {
    clearErrors,
    register,
    handleSubmit,
    errors,
    setError,
    setValue,
    watch,
    formState: { isSubmitting },
  } = useForm();

  const navigate = useNavigate();
  const [{ signup }, dispatch] = useGlobalState();
  const { error: signupError } = signup;
  const hostname = watch('hostname');
  const [validating, setValidating] = useState(false);
  const canSubmit = !isSubmitting && !validating && isEmpty(errors);
  const clearApiError = () => setApiError(null);
  window.dataLayer = window.dataLayer || [];

  const validateHostname = (hostname) => {
    if (hostnamePrefixRegex.test(hostname)) {
      return axios
        .get(`/xm/free/shared/api/hostname/${encodeURIComponent(hostname)}`)
        .then(() => {
          setError('hostname', {
            type: 'duplicate',
            message: hostnameExistedMsg,
          });
        })
        .catch((error) => {
          const errorCode = error?.response?.status;
          if (errorCode === 400) {
            setError('hostname', {
              type: 'malformed',
              message: hostnameMalformedMsg,
            });
          } else if (errorCode !== 404) {
            setApiError(error?.json?.message || unexpectedServerErrorMsg);
          } else {
            clearApiError();
          }
        })
        .finally(() => setValidating(false));
    }
    const hostnameTooShort = hostname.length < 2;
    setError('hostname', {
      type: hostnameTooShort ? 'duplicate' : 'malformed',
      message: hostnameTooShort ? hostnameExistedMsg : hostnameMalformedMsg,
    });
    setValidating(false);
  };

  const debouncedValidateHostname = useDebounceFn(validateHostname, 500);

  useEffect(() => {
    if (hostname?.trim()?.length) {
      setValidating(true);
      clearErrors('hostname');
      debouncedValidateHostname(hostname);
    }
  }, [hostname, debouncedValidateHostname, clearErrors]);

  useEffect(() => {
    if (signupError) {
      if (signupError?.response?.status >= 500) {
        setApiError(unexpectedServerErrorMsg);
      } else
        setApiError(
          signupError?.response?.data?.message || unexpectedServerErrorMsg,
        );
    }
  }, [signupError, setApiError]);

  const onSubmit = (data) => {
    clearApiError();
    const signupPayload = { ...signupData };
    delete signupPayload.region;
    dispatch({
      type: 'SIGNUP_PENDING',
      payload: data.hostname,
    });
    dispatch({
      type: 'SET_WAITING_SCREEN_VISIBLE',
      payload: true,
    });
    return postForm({
      signup: signupPayload,
      hostnamePrefix: data.hostname,
      password: data.password,
    })
      .then((res) => {
        window.dataLayer.push({
          event: 'xm_signupComplete',
        });
        dispatch({
          type: 'SIGNUP_COMPLETE',
          payload: res.headers.location,
        });
        navigate('/awaitProvision');
      })
      .catch((error) => {
        // COR-33759: treat timeout as a non-failure
        if ([408, 504].includes(error?.response?.status)) {
          window.dataLayer.push({
            event: 'xm_signupComplete',
          });
          dispatch({
            type: 'SIGNUP_COMPLETE',
            payload: `https://${
              data.hostname
            }${window.location.hostname.replace('provision', '')}`,
          });
          navigate('/awaitProvision');
        } else {
          dispatch({
            type: 'SIGNUP_ERROR',
            payload: error,
          });
          dispatch({
            type: 'SET_WAITING_SCREEN_VISIBLE',
            payload: false,
          });
          if (error?.response?.status === 500) {
            navigate('/');
          }
        }
      });
  };

  return (
    <form
      className={styles.onboardingForm}
      onSubmit={handleSubmit(onSubmit)}
      id="onboardingForm"
    >
      <InputField
        autoComplete="username"
        error={errors.hostname}
        fieldName="hostname"
        inputRef={register({ required: requiredErrorMsg })}
        labelText="Hostname"
        maxLength={46}
        placeholder="your-company"
        testid="onboardingForm_hostname"
        trailingText=".xmatters.com"
        validating={validating}
        onChange={(e) => setValue('hostname', e.target.value.trim())}
      />
      <InputField
        autoComplete="new-password"
        error={errors.password}
        fieldName="password"
        inputRef={register({
          required: requiredErrorMsg,
        })}
        labelText="Password"
        maxLength={100}
        testid="onboardingForm_password"
        type="password"
      />
      <InputField
        autoComplete="new-password"
        error={errors.confirmPassword}
        fieldName="confirmPassword"
        inputRef={register({
          required: requiredErrorMsg,
          validate: (value) =>
            value === watch('password') || passwordNotMatchMsg,
        })}
        labelText="Verify Password"
        maxLength={100}
        testid="onboardingForm_confirmPassword"
        type="password"
      />

      <div className={styles.btnWrap}>
        <Button
          className={styles.button}
          type="submit"
          color="primary"
          disabled={!canSubmit}
          data-testid="onboardingForm_btn"
        >
          {isSubmitting ? 'Please wait' : 'Create Account'}
        </Button>
      </div>
    </form>
  );
}

OnboardingForm.propTypes = {
  signupData: PropTypes.shape({
    token: PropTypes.string.isRequired,
  }),
  setApiError: PropTypes.func.isRequired,
};

OnboardingForm.defaultProps = {
  signupData: null,
};
